// SEO Configuration and Utilities for Systems Authority

export const DEFAULT_SEO = {
  title: 'b2b.codes - B2B Data Intelligence & Smart Marketing Automation',
  description: 'Transform B2B marketing with in-market visitor data, intelligent email automation, e-commerce optimization, and accessibility solutions. Data-driven insights for smarter business decisions.',
  keywords: 'B2B data intelligence, in-market visitor tracking, smart email automation, e-commerce data, accessibility compliance, business services, site visitor analytics, buying intent signals',
  author: 'b2b.codes',
  siteUrl: 'https://b2b.codes',
  twitterHandle: '@b2bcodes',
  ogImage: '/assets/images/og-image.jpg',
  organizationName: 'b2b.codes',
};

// Page-specific SEO configurations
export const PAGE_SEO = {
  '/': {
    title: 'b2b.codes - Smart B2B Data Intelligence & Marketing Automation',
    description: 'Leverage in-market visitor data, intelligent email campaigns, e-commerce analytics, and accessibility compliance to transform your B2B marketing strategy. Data-driven solutions for modern businesses.',
    keywords: 'B2B data intelligence, in-market visitor data, smart email marketing, site visitor analytics, e-commerce optimization, accessibility solutions, buying intent, business services',
    schema: 'Organization',
  },
  '/knowledge-hub': {
    title: 'Knowledge Hub - Expert Insights & Best Practices | B2B Codes',
    description: 'Access comprehensive guides, case studies, and expert insights on data management, accessibility standards, SaaS optimization, and e-commerce solutions.',
    keywords: 'business insights, data best practices, accessibility guides, SaaS optimization, e-commerce strategies',
    schema: 'Blog',
  },
  '/solution-finder-tool': {
    title: 'Solution Finder Tool - Discover Your Perfect Business Solution | B2B Codes',
    description: 'Interactive assessment tool to discover tailored solutions for your business needs in data, accessibility, SaaS, e-commerce, and beyond.',
    keywords: 'solution finder, business assessment, needs analysis, custom solutions',
    schema: 'WebApplication',
  },
  '/solutions-categories': {
    title: 'Solutions & Services - Comprehensive Business Solutions | B2B Codes',
    description: 'Explore our comprehensive solutions for data integration, accessibility compliance, SaaS optimization, e-commerce platforms, and business process automation.',
    keywords: 'business solutions, data services, accessibility services, SaaS consulting, e-commerce solutions',
    schema: 'Service',
  },
  '/resource-library': {
    title: 'Resource Library - Downloadable Guides & Templates | B2B Codes',
    description: 'Download free resources, templates, and guides for data management, accessibility compliance, SaaS implementation, and e-commerce optimization.',
    keywords: 'business resources, downloadable guides, templates, checklists, whitepapers',
    schema: 'Collection',
  },
  '/consultation-gateway': {
    title: 'Book Consultation - Expert Business Systems Consulting | B2B Codes',
    description: 'Schedule a free consultation with our experts to discuss your data, accessibility, SaaS, e-commerce, or digital transformation needs.',
    keywords: 'consultation, expert advice, business consulting, free consultation',
    schema: 'Service',
  },
  '/authority-center': {
    title: 'Authority Center - Our Credentials & Expertise | B2B Codes',
    description: 'Learn about our certifications, case studies, and proven track record in delivering data, accessibility, SaaS, and e-commerce solutions.',
    keywords: 'certifications, case studies, credentials, expertise, testimonials',
    schema: 'AboutPage',
  },
  '/wiki-page': {
    title: 'Living Wiki System - Community Knowledge Base | B2B Codes',
    description: 'Collaborative knowledge base powered by AI conversations covering data integration, accessibility, SaaS, e-commerce, and business systems.',
    keywords: 'knowledge base, wiki, community resources, AI-powered knowledge',
    schema: 'FAQPage',
  },
  '/admin-dashboard': {
    title: 'Admin Dashboard - Content & Analytics Management | B2B Codes',
    description: 'Comprehensive admin dashboard for content management, SEO optimization, analytics, and lead tracking.',
    keywords: 'admin dashboard, content management, analytics, SEO management',
    schema: 'WebApplication',
  },
};

// Generate JSON-LD structured data
export const generateStructuredData = (pageKey, customData = {}) => {
  const pageSEO = PAGE_SEO?.[pageKey] || {};
  const baseData = {
    '@context': 'https://schema.org',
    '@type': pageSEO?.schema || 'WebPage',
    name: pageSEO?.title || DEFAULT_SEO?.title,
    description: pageSEO?.description || DEFAULT_SEO?.description,
    url: `${DEFAULT_SEO?.siteUrl}${pageKey}`,
    publisher: {
      '@type': 'Organization',
      name: DEFAULT_SEO?.organizationName,
      logo: {
        '@type': 'ImageObject',
        url: `${DEFAULT_SEO?.siteUrl}/favicon.ico`,
      },
    },
    ...customData,
  };

  // Add organization-specific data for homepage with B2B focus
  if (pageKey === '/') {
    return {
      ...baseData,
      '@type': 'Organization',
      name: DEFAULT_SEO?.organizationName,
      url: DEFAULT_SEO?.siteUrl,
      logo: `${DEFAULT_SEO?.siteUrl}/favicon.ico`,
      description: DEFAULT_SEO?.description,
      sameAs: [
        'https://twitter.com/b2bcodes',
        'https://linkedin.com/company/b2bcodes',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-555-0100',
        contactType: 'Customer Service',
        areaServed: 'US',
        availableLanguage: ['English'],
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
        worstRating: '1',
      },
      serviceArea: {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: '37.7749',
          longitude: '-122.4194',
        },
      },
      knowsAbout: [
        'B2B Data Intelligence',
        'In-Market Visitor Tracking',
        'Smart Email Automation',
        'E-commerce Analytics',
        'Accessibility Compliance',
        'Business Services',
      ],
    };
  }

  return baseData;
};

// Generate sitemap data
export const SITEMAP_URLS = Object.keys(PAGE_SEO)?.map((path) => ({
  url: `${DEFAULT_SEO?.siteUrl}${path}`,
  changefreq: path === '/' ? 'daily' : 'weekly',
  priority: path === '/' ? '1.0' : '0.8',
  lastmod: new Date()?.toISOString()?.split('T')?.[0],
}));

// SEO Meta Tags Component Helper
export const getSEOTags = (pageKey, customSEO = {}) => {
  const pageSEO = PAGE_SEO?.[pageKey] || {};
  const seo = {
    title: customSEO?.title || pageSEO?.title || DEFAULT_SEO?.title,
    description: customSEO?.description || pageSEO?.description || DEFAULT_SEO?.description,
    keywords: customSEO?.keywords || pageSEO?.keywords || DEFAULT_SEO?.keywords,
    ogImage: customSEO?.ogImage || DEFAULT_SEO?.ogImage,
    canonical: `${DEFAULT_SEO?.siteUrl}${pageKey}`,
  };

  return {
    title: seo?.title,
    meta: [
      { name: 'description', content: seo?.description },
      { name: 'keywords', content: seo?.keywords },
      { name: 'author', content: DEFAULT_SEO?.author },
      { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
      
      // Open Graph
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: seo?.canonical },
      { property: 'og:title', content: seo?.title },
      { property: 'og:description', content: seo?.description },
      { property: 'og:image', content: `${DEFAULT_SEO?.siteUrl}${seo?.ogImage}` },
      { property: 'og:site_name', content: DEFAULT_SEO?.organizationName },
      
      // Twitter Card
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: DEFAULT_SEO?.twitterHandle },
      { name: 'twitter:title', content: seo?.title },
      { name: 'twitter:description', content: seo?.description },
      { name: 'twitter:image', content: `${DEFAULT_SEO?.siteUrl}${seo?.ogImage}` },
    ],
    link: [
      { rel: 'canonical', href: seo?.canonical },
    ],
  };
};